import { useCallback, useState } from 'react';

import {
    addDistributionTransfer,
    deleteDistributionTransfer,
    type DistributionPlanDetail,
    type DistributionTransferRow,
} from '@api/distribution';
import { showError, showSuccess } from '@utils/notifications';

type UndoOp =
    | { type: 'REMOVE'; transferId: string }
    | {
          type: 'ADD';
          productId: string;
          productName: string | null;
          fromWarehouseId: string;
          toWarehouseId: string | null;
          quantity: number;
      };

export function usePlanEdits(
    planId: string,
    onPlanUpdated: (updater: (prev: DistributionPlanDetail) => DistributionPlanDetail) => void,
) {
    const [undoStack, setUndoStack] = useState<UndoOp[]>([]);
    const [undoing, setUndoing] = useState(false);

    const addSku = useCallback(
        async (
            productId: string,
            productName: string | null,
            fromWarehouseId: string,
            toWarehouseId: string | null,
            quantity: number,
        ): Promise<void> => {
            try {
                const newTransfer = await addDistributionTransfer(planId, {
                    productId,
                    productName,
                    fromWarehouseId,
                    toWarehouseId,
                    quantity,
                });
                onPlanUpdated(plan => ({
                    ...plan,
                    transfers: [...plan.transfers, newTransfer],
                    lineCount: plan.lineCount + 1,
                }));
                setUndoStack(prev => [...prev, { type: 'REMOVE', transferId: newTransfer.id }]);
                showSuccess('Added to plan');
            } catch {
                showError('Failed to add SKU. Please try again.');
            }
        },
        [planId, onPlanUpdated],
    );

    const removeSku = useCallback(
        async (transfer: DistributionTransferRow): Promise<void> => {
            try {
                await deleteDistributionTransfer(transfer.id);
                onPlanUpdated(plan => ({
                    ...plan,
                    transfers: plan.transfers.filter(t => t.id !== transfer.id),
                    lineCount: Math.max(0, plan.lineCount - 1),
                }));
                setUndoStack(prev => [
                    ...prev,
                    {
                        type: 'ADD',
                        productId: transfer.productId,
                        productName: transfer.productName,
                        fromWarehouseId: transfer.fromWarehouseId,
                        toWarehouseId: transfer.toWarehouseId,
                        quantity: transfer.approvedQty ?? transfer.recommendedQty,
                    },
                ]);
                showSuccess('Removed from plan');
            } catch {
                showError('Failed to remove transfer. Please try again.');
            }
        },
        [onPlanUpdated],
    );

    const undo = useCallback(async (): Promise<void> => {
        if (!undoStack.length || undoing) return;
        const op = undoStack[undoStack.length - 1];
        setUndoing(true);
        try {
            if (op.type === 'REMOVE') {
                await deleteDistributionTransfer(op.transferId);
                onPlanUpdated(plan => ({
                    ...plan,
                    transfers: plan.transfers.filter(t => t.id !== op.transferId),
                    lineCount: Math.max(0, plan.lineCount - 1),
                }));
            } else {
                const newTransfer = await addDistributionTransfer(planId, {
                    productId: op.productId,
                    productName: op.productName,
                    fromWarehouseId: op.fromWarehouseId,
                    toWarehouseId: op.toWarehouseId,
                    quantity: op.quantity,
                });
                onPlanUpdated(plan => ({
                    ...plan,
                    transfers: [...plan.transfers, newTransfer],
                    lineCount: plan.lineCount + 1,
                }));
            }
            setUndoStack(prev => prev.slice(0, -1));
        } catch {
            showError('Undo failed. Please try again.');
        } finally {
            setUndoing(false);
        }
    }, [undoStack, undoing, planId, onPlanUpdated]);

    const clearUndoStack = useCallback(() => setUndoStack([]), []);

    return {
        addSku,
        removeSku,
        undo,
        clearUndoStack,
        canUndo: undoStack.length > 0,
        undoStackSize: undoStack.length,
        undoing,
    };
}
